import * as React from 'react';
import { useEffect, useState } from 'react';
import { Button, Select, Input, Spin, DatePicker, Row, Col, Space } from 'antd';
import { Dayjs } from 'dayjs';

import { fetchPrescriptionLogs, downloadPrescriptionLogsExcel } from "../../api/prescription-log";
import { PrescriptionLogsData } from "../../types/PrescriptionLogsData";
import PrescriptionLogsTable from "./PrescriptionLogsTable";
import TablePagination from "../TablePagination";
import { parseDate } from "../../util/date";
import { useTexts } from "../../hooks/useTexts";
import { downloadExcelData } from "../../data/util";

import "../PrescriptionView/styles.css";

interface SelectedFilters {
    userName: string;
    phone: string;
    cDtFrom: Dayjs | null;
    cDtTo: Dayjs | null;
    page: number;
    pageSize: number;
    sortBy: string;
    sortDir: string;
}

const initialFilters: SelectedFilters = {
    userName: "",
    phone: "",
    cDtFrom: null,
    cDtTo: null,
    page: 1,
    pageSize: 50,
    sortBy: "log.createdAt",
    sortDir: "DESC"
};

const PrescriptionLogsView = (): JSX.Element => {
    const { t } = useTexts();
    const [data, setData] = useState<PrescriptionLogsData>({
        items: [],
        pagination: {
            page: 0,
            pageSize: 50,
            pageCount: 0,
            elementCount: 0,
        }
    });

    const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>(initialFilters);
    const [loading, setLoading] = useState(false);

    const getParams = (filters: SelectedFilters) => {
        return {
            userName: filters.userName || null,
            phone: filters.phone || null,
            cDtFrom: parseDate(filters.cDtFrom),
            cDtTo: parseDate(filters.cDtTo),
            page: filters.page,
            pageSize: filters.pageSize,
            sortBy: filters.sortBy,
            sortDir: filters.sortDir
        };
    };

    const fetchData = async (filters: SelectedFilters) => {
        setLoading(true);
        const params = getParams(filters);
        try {
            const result = await fetchPrescriptionLogs(params);
            if (result) {
                setData(result);
            }
        } catch (err) {
            console.error("Error fetching logs:", err);
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchData(selectedFilters);
    }, [selectedFilters.sortBy, selectedFilters.sortDir]);

    const applyFilters = () => {
        fetchData({ ...selectedFilters, page: 1 });
    };

    const clearFilter = () => {
        setSelectedFilters(initialFilters);
        fetchData(initialFilters);
    };

    const downloadExcel = async (filters: SelectedFilters) => {
        const params = getParams(filters);
        const data = await downloadPrescriptionLogsExcel(params);
        downloadExcelData(data, "Gonderisler_Log");
    };

    return (
        <>
            <div style={{ background: '#f5f5f5', padding: '8px', borderRadius: '8px', marginBottom: '24px' }}>
                <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                    <Col xs={24} sm={12} md={5}>
                        ≈
                    </Col>
                    <Col xs={24} sm={12} md={5}>
                        <Input
                            placeholder={t('prescription', 'contactNumber') || 'Phone / Additional Phone'}
                            value={selectedFilters.phone}
                            onChange={(e) => setSelectedFilters({ ...selectedFilters, phone: e.target.value })}
                            style={{ width: '100%' }}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={4}>
                        <DatePicker
                            placeholder={t('prescription', 'createdDateFrom') || 'Date From'}
                            value={selectedFilters.cDtFrom}
                            onChange={(newValue) => setSelectedFilters({ ...selectedFilters, cDtFrom: newValue })}
                            style={{ width: '100%' }}
                            format="DD/MM/YYYY"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={4}>
                        <DatePicker
                            placeholder={t('prescription', 'createdDateTo') || 'Date To'}
                            value={selectedFilters.cDtTo}
                            onChange={(newValue) => setSelectedFilters({ ...selectedFilters, cDtTo: newValue })}
                            style={{ width: '100%' }}
                            format="DD/MM/YYYY"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Space.Compact style={{ width: '100%' }}>
                            <Select
                                style={{ width: '55%' }}
                                placeholder={t('common', 'sort') || 'Sort By'}
                                value={selectedFilters.sortBy}
                                onChange={(value) => setSelectedFilters({ ...selectedFilters, sortBy: value })}
                                options={[
                                    { value: 'log.createdAt', label: t('prescription', 'sortByCreatedDate') || 'Created Date' },
                                    { value: 'user.username', label: t('prescription', 'user') || 'User' }
                                ]}
                            />
                            <Select
                                style={{ width: '45%' }}
                                placeholder={t('common', 'direction') || 'Direction'}
                                value={selectedFilters.sortDir}
                                onChange={(value) => setSelectedFilters({ ...selectedFilters, sortDir: value })}
                                options={[
                                    { value: 'ASC', label: t('common', 'ascending') || 'Ascending' },
                                    { value: 'DESC', label: t('common', 'descending') || 'Descending' }
                                ]}
                            />
                        </Space.Compact>
                    </Col>
                </Row>
                <Row justify="end">
                    <Col>
                        <Space wrap style={{ justifyContent: 'flex-end' }}>
                            <Button onClick={() => downloadExcel({ ...selectedFilters, pageSize: 1000000 })}>
                                {t('prescription', 'downloadExcel') || 'Download Excel'}
                            </Button>
                            <Button
                                type="primary"
                                onClick={applyFilters}
                                loading={loading}
                            >
                                {loading ? <Spin size="small" /> : t('prescription', 'applyFilter') || 'Apply'}
                            </Button>
                            <Button
                                danger
                                onClick={clearFilter}
                            >
                                {t('prescription', 'clearFilter') || 'Clear'}
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </div>

            <PrescriptionLogsTable data={data} />

            <TablePagination
                current={data.pagination.page}
                pageSize={data.pagination.pageSize}
                pageCount={data.pagination.pageCount}
                total={data.pagination.elementCount}
                onChange={(page: number, pageSize: number) => {
                    const newFilters = { ...selectedFilters, page, pageSize };
                    setSelectedFilters(newFilters);
                    fetchData(newFilters);
                }}
            />
        </>
    );
};

export default PrescriptionLogsView;
